/*=== CORE IMPORTS ===*/
import { LitElement, html } from 'lit';
import { storageBaseUrl, dbUrlTemplate } from '../firebaseConfig.js';
import { viewStyles } from '../styles/view-styles.js';

/*=== Q1 COMPONENTS ===*/
import '../components/q1-app-header/q1-app-header.js';
import '../components/q1-app-footer/q1-app-footer.js';
import { updateMetaFromContext, setErrorMeta } from '../modules/meta-registry.js';
import { initMetaBuilders } from '../models/meta-client-builders.js';

/*=== VIEW CLASS ===*/

export class ViewBase extends LitElement {

  static styles = [
    viewStyles,
  ];

  static properties = {
    isSignedIn: { type: Boolean },
    currentUser: { type: Object },
    currentMember: { type: Object },
    isAdmin: { type: Boolean },

    cmImage_profile: { type: Object },
    cmImage_banner: { type: Object },

    routeParams: { type: Object },
    urlParams: { type: Object },
    appElement: { type: Object },
  };

  constructor() {
    super();

    this.currentUser = null;
    this.currentMember = null;
    this.isSignedIn = false;
    this.isAdmin = false;

    this.routeParams = {};
    this.urlParams = Object.fromEntries(new URLSearchParams(window.location.search));
    this.appElement = null;
  }

  /*=== LIFECYCLE ===*/

  connectedCallback() {
    super.connectedCallback();

    this.urlParams = Object.fromEntries(new URLSearchParams(window.location.search));
    this.appElement = document.querySelector('q1-home');
    initMetaBuilders();
  }

  updated(changedProperties) {
    if (changedProperties.has('currentMember') && this.currentMember) {
      this.cmImage_profile = {
        mini: this.docImageUrl(this.currentMember, 'profile', 'mini'),
        small: this.docImageUrl(this.currentMember, 'profile', 'small'),
        standard: this.docImageUrl(this.currentMember, 'profile', 'standard'),
      };
      this.cmImage_banner = {
        mini: this.docImageUrl(this.currentMember, 'banner', 'mini'),
        small: this.docImageUrl(this.currentMember, 'banner', 'small'),
        standard: this.docImageUrl(this.currentMember, 'banner', 'standard'),
      };

      const context = typeof this.metaContext === 'function'
      ? this.metaContext()
      : this.metaContext;

      if (context && Object.values(context).every(Boolean)) {
        this.setViewMeta();
      } 
    }
  }

  /**
   * Call this in the view constructor to initialize the `initComplete` promise.
   * This is useful for child views to await initialization completion.
   * 
   * Resets the `initComplete` promise. Child views must call `this._resolveInit()` when 
   * initialization is complete if they want to use it.
   * 
   * Then other initialization logic that needs to wait until first init complete can use
   * the `await this.initComplete` pattern.
   */
  resetInitParams() {
    this.initComplete = new Promise((resolve, reject) => {
      this._resolveInit = resolve;
      this._rejectInit = reject;
    });
  }

  /*=== ACTIONS ===*/

  /**
   * Use this to navigate to a particular path (`/projects/123`, `/home`, etc) from code.
   * Do not use this for direct user-click-driven navigation. For that just use `<a href="...">`.
   * This is an alternative to `window.location.href = '...'` which uses the app router and 
   * avoids a full page reload.
  */
  navigateTo(path) {
    const appElement = document.querySelector('q1-home');

    if (appElement) {
      appElement.navigateTo(path);
    }
  }

  /**
   * Adds a toast alert to the toast stack using `<sl-alert>`
   * 
   * @param {*} message - The message to display.
   * @param {*} title - Optional. If included, this is added as the first line, in bold.
   * @param {string} variant - `primary`, `success`, `neutral`, `warning`, `danger`
   * @param {*} duration - Time to display in milliseconds.
   */
  showToast(message, title = undefined, variant = undefined, duration = undefined) {
    const appElement = document.querySelector('q1-home');

    if (appElement) {
      appElement.showToast(message, title, variant, duration);
    }
  }

  setViewMeta(docTitle = null) {
    const tagName = this.tagName?.toLowerCase();
    const context = (typeof this.metaContext === 'function')
      ? this.metaContext()
      : (this.metaContext || {});
  
    if (docTitle) {
      context.docTitle = docTitle;
    }
  
    updateMetaFromContext(tagName, context);
  }
  
  setErrorViewMeta(title = 'Error', description = 'Something went wrong.') {
    setErrorMeta({
      title: `${title} - q1 Network`,
      description,
      image: '/assets/images/q1-banner.png',
      url: window.location.origin + window.location.pathname,
    });
  }
  

  setAfLink(link) {
    if (this.isSignedIn) this.renderRoot.querySelector('q1-app-footer').setupAfLink(link);
  }
  setAfLink_db(collection, docId) {
    this.setAfLink(dbUrlTemplate(collection, docId));
  }

  
  /*=== HANDLERS ===*/

  handleInputChange(e) {
    const { name, value } = e.target;
    if (this.selectedMember) {
      this.selectedMember = { ...this.selectedMember, [name]: value };
    }
  }  

  /*=== VIEW SETTINGS TO OVERWRITE ===*/
  
  /**
   * Optional. This is passed to the `selected` property of `q1-app-header`.
   * Values: `guilds`, `projects`, `members`.
   */
  get appHeaderSelected() {
    return '';
  }

  /** Optional. Overwrite this to change the variant of the `q1-app-header` */
  get appHeaderVariant() {
    return 'default';
  }

  /** Optional. Overwrite this to change the variant of the `q1-app-footer` */
  get appFooterVariant() {
    return 'default';
  }

  /** Optional. Overwrite this to set `class="___"` on the `<view-content>` element. */
  get viewContentStyleClasses() {
    return '';
  }

  /*=== GETTERS ===*/

  /**
   * The app persona is a standardized way to determine the context of the current app viewer.
   * - `public` - Not signed in
   * - `network` - Signed in as network level member
   * - `contributor` - Signed in as contributor level member
   * - `foundation` - Signed in as foundation level member
   * - `steward` - Signed in as steward level member
   */
  get appPersona() {
    if (!this.currentMember?.level) return 'public';
    return this.currentMember.level;
  }

  /*=== CORE VIEW TEMPLATES TO OVERWRITE ===*/

  /**
   * Required. This is the primary content area of the view. Usually this is the only render 
   * method you will want to overwrite.
   */
  get contentTemplate() {
    return html``;
  }

  /** Optional. Only overwrite if you want to have a custom header. */
  get headerTemplate() {
    return html`
      <q1-app-header
        .currentUser=${this.currentUser}
        .currentMember=${this.currentMember}
        .isSignedIn=${this.isSignedIn}
        .isAdmin=${this.isAdmin}
        variant=${this.appHeaderVariant}
        selected=${this.appHeaderSelected}
      ></q1-app-header>
    `;
  }

  /** Optional. Only overwrite if you want to have a custom footer. */
  get footerTemplate() {
    return html`
      <q1-app-footer variant=${this.appFooterVariant}></q1-app-footer>
    `;
  }

  /*=== RENDER ===*/

  /**
   * You shouldn't need to actually overwrite this method. Typically you'll just overwrite 
   * `contentTemplate`, and more rarely `headerTemplate` and/or `footerTemplate`.
   */
  render() {
    return html`

      <view-header>
        ${this.headerTemplate}
      </view-header>

      <view-content class=${this.viewContentStyleClasses}>
        ${this.contentTemplate}
      </view-content>
      
      <view-footer>
        ${this.footerTemplate}
      </view-footer>

    `;
  }  

  /*=== UTILITIES ===*/

  /**
   * Scrolls the window to center the element specified by the querySelector.
   * @param {string} querySelector - The selector for the element to center in the viewport.
   * @returns {Object} - The element that was scrolled to, or null if not found.
   */
  scrollToCenterOf(querySelector) {
    const element = this.renderRoot.querySelector(querySelector);
    if (!element) {
      console.warn(`scrollToCenterOf: Element not found for selector: ${querySelector}`);
      return null;
    }

    const elementTop = element.offsetTop;
    const elementHeight = element.offsetHeight;
    const windowHeight = window.innerHeight;
    const scrollTo = elementTop - (windowHeight / 2) + (elementHeight / 2);
    window.scrollTo({ top: scrollTo, behavior: 'smooth' });

    return element;
  }

  /** Scrolls to the top of the view. */
  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  
  /** Returns the image URL from a doc, by providing the image field key. */
  docImageUrl(doc, imageField, sizeKey) {
    const imageFieldValue = doc?.[imageField];
    return this.imageUrl(imageFieldValue, sizeKey);
  }

  /**
   * Use this method in a view to listen for 'Enter' key presses in an sl-dialog and invoke the 
   * specified function when the enter key is pressed.
   * 
   * This method is safe to run on the same dialog element an arbitrary number of times.
   * It sets `dialogElement.dataset.q1EnterRegistered` to true to avoid registering multiple
   * listeners on the same dialog element.
   * 
   * @param {String} dialogId 
   * @param {Function} functionToExecute 
   * @param {Array<String>} suppressElements - Array of elements which should suppress the 
   *   functionality if the current active element is of that tagname.
   */
  registerEnterListenerOnDialog(
    dialogId, functionToExecute,
    suppressElements = ['SL-TEXTAREA', 'TEXTAREA']
  ) {
    const dialogElement = this.renderRoot.getElementById(dialogId);

    if (dialogElement?.dataset && !dialogElement.dataset.q1EnterRegistered) {
      dialogElement.addEventListener('keydown', (event) => {
        if (
          (event.key === 'Enter')
          && !suppressElements.includes(this.renderRoot.activeElement.tagName)
        ) {
          event.preventDefault();
          functionToExecute();
        }
      });
      dialogElement.dataset.q1EnterRegistered = true;
    }
  }

  /** Returns the image URL from a image object. */
  imageUrl(imageFieldValue, sizeKey) {
    const imagePath_raw = imageFieldValue?.[sizeKey]?.path;
    if (!imagePath_raw) return null;

    const imagePath_clean = imagePath_raw.startsWith('/')
      ? imagePath_raw.substring(1)
      : imagePath_raw;

    const imagePath_encoded = encodeURIComponent(imagePath_clean);
    
    return `${storageBaseUrl}/${imagePath_encoded}?alt=media`;
  }
  
}